// History of a machine in the app (FR-26, FR-27) and its cloud-owned data (AD-7, AD-44): the first page after every
// (re)connect, more pages on demand, new shots from the WebSocket, rating/note/deletion/reference over HTTP. Every
// change of a shot or the reference is taken only with a higher cloudRev than the app knows for it, whether it comes as
// HTTP answer or as WebSocket delta, so several open apps always end on the same state (FR-21).
import {
  type ReferenceMessage,
  type ReferenceResponse,
  type ShotDeltaMessage,
  type ShotItem,
  type ShotListResponse,
  type ShotSummaryResponse,
  type ShotUpdateMessage,
} from '@brewcore/app-contract';

import { jsonRequest, type FetchLike, type HttpResult, type HttpTarget } from './http.js';

export type { FetchLike, HttpResult } from './http.js';

/** REST of the history (app-v1 http-shots, http-shot, http-reference). */
export class ShotsApi {
  private readonly target: HttpTarget;

  constructor(
    fetchImpl: FetchLike = (input, init) => fetch(input, init),
    base = '',
    headers: Record<string, string> = {},
  ) {
    this.target = { fetchImpl, base, headers };
  }

  /** One page newest first; `filter` are the query parameters of the history's filters (core/shot-history.ts). */
  list(
    machine: string,
    before: string | null,
    limit = 20,
    filter: Record<string, string> = {},
  ): Promise<HttpResult<ShotListResponse>> {
    const q = new URLSearchParams({ limit: String(limit), ...filter, ...(before ? { before } : {}) });
    return this.call('GET', `/api/machines/${machine}/shots?${q}`, null, 'http-shots');
  }

  summary(machine: string, filter: Record<string, string> = {}): Promise<HttpResult<ShotSummaryResponse>> {
    const q = new URLSearchParams(filter);
    return this.call('GET', `/api/machines/${machine}/shots/summary?${q}`, null, 'http-shot-summary');
  }

  get(machine: string, id: string): Promise<HttpResult<ShotItem>> {
    return this.call('GET', `/api/machines/${machine}/shots/${id}`, null, 'http-shot');
  }

  update(
    machine: string,
    id: string,
    change: { rating?: number | null; note?: string | null; tags?: string[]; ifRev?: number },
  ): Promise<HttpResult<ShotItem>> {
    return this.call('PATCH', `/api/machines/${machine}/shots/${id}`, change, 'http-shot');
  }

  remove(machine: string, id: string): Promise<HttpResult<null>> {
    return this.call('DELETE', `/api/machines/${machine}/shots/${id}`, null, null);
  }

  reference(machine: string): Promise<HttpResult<ReferenceResponse>> {
    return this.call('GET', `/api/machines/${machine}/reference`, null, 'http-reference');
  }

  setReference(machine: string, shotId: string | null): Promise<HttpResult<ReferenceResponse>> {
    return this.call('PUT', `/api/machines/${machine}/reference`, { shotId }, 'http-reference');
  }

  private call<T>(
    method: string,
    path: string,
    body: unknown,
    schema: 'http-shots' | 'http-shot' | 'http-shot-summary' | 'http-reference' | null,
  ): Promise<HttpResult<T>> {
    return jsonRequest<T>(this.target, method, path, body, schema);
  }
}

/** What changes of a shot after it arrived (cloud-owned, AD-7). */
export interface ShotChange {
  deleted?: boolean;
  rating?: number | null;
  note?: string | null;
  tags?: string[];
  tagsAuto?: string[];
}

export interface ShotsView {
  /** newest first */
  items: readonly ShotItem[];
  loaded: boolean;
  loading: boolean;
  /** cursor of the next page, null at the end */
  next: string | null;
  reference: { shotId: string | null; cloudRev: number; shot: ShotItem | null };
  /** curves fetched so far, by shot ID */
  curves: Readonly<Record<string, ShotItem>>;
  error: string | null;
}

const sortKey = (s: ShotItem): number => (s.occurredAt !== null ? s.occurredAt * 1000 : s.receivedAt);

export class ShotsStore {
  private view: ShotsView = {
    items: [],
    loaded: false,
    loading: false,
    next: null,
    reference: { shotId: null, cloudRev: -1, shot: null },
    curves: {},
    error: null,
  };
  private readonly listeners = new Set<() => void>();
  /** highest cloudRev seen per shot, also for deleted ones (a late HTTP answer must not bring them back) */
  private readonly revs = new Map<string, number>();
  private readonly deleted = new Set<string>();
  private refreshing: Promise<void> | null = null;
  private readonly changeListeners = new Set<(id: string, cloudRev: number, change: ShotChange) => void>();

  constructor(
    readonly machineId: string,
    private readonly api: ShotsApi,
  ) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ShotsView => this.view;

  private commit(patch: Partial<ShotsView>): void {
    this.view = { ...this.view, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** First page and reference, after every snapshot (the deltas missed while disconnected, FR-21). */
  refresh(): Promise<void> {
    this.refreshing ??= this.load().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async load(): Promise<void> {
    this.commit({ loading: true });
    const [list, ref] = await Promise.all([
      this.api.list(this.machineId, null),
      this.api.reference(this.machineId),
    ]);
    if (list.ok) {
      const fresh = list.value.items.filter((s) => !this.deleted.has(s.id));
      // shots of the first page replace what the app had in that range; older pages stay
      const oldest = fresh.length ? Math.min(...fresh.map(sortKey)) : -Infinity;
      const keep = list.value.next ? this.view.items.filter((s) => sortKey(s) < oldest) : [];
      const items: ShotItem[] = [];
      for (const s of fresh) items.push(this.newer(s) ?? s);
      this.commit({
        items: this.merge([...items, ...keep], []),
        next: keep.length ? this.view.next : list.value.next,
        loaded: true,
        error: null,
      });
    } else this.commit({ error: list.error.code });
    if (ref.ok) this.applyReference(ref.value.shot?.id ?? null, ref.value.cloudRev, ref.value.shot);
    this.commit({ loading: false });
  }

  async loadMore(): Promise<void> {
    if (!this.view.next || this.view.loading) return;
    this.commit({ loading: true });
    const r = await this.api.list(this.machineId, this.view.next);
    if (r.ok) {
      const items = r.value.items.filter((s) => !this.deleted.has(s.id)).map((s) => this.newer(s) ?? s);
      this.commit({ items: this.merge(this.view.items, items), next: r.value.next, error: null });
    } else this.commit({ error: r.error.code });
    this.commit({ loading: false });
  }

  /** The stored version if it is newer than s (by cloudRev), else records s's revision and returns null. */
  private newer(s: ShotItem): ShotItem | null {
    const known = this.revs.get(s.id);
    if (known !== undefined && known > s.cloudRev) return this.view.items.find((x) => x.id === s.id) ?? null;
    this.revs.set(s.id, s.cloudRev);
    return null;
  }

  private merge(a: readonly ShotItem[], b: readonly ShotItem[]): ShotItem[] {
    const byId = new Map<string, ShotItem>();
    for (const s of [...a, ...b]) if (!this.deleted.has(s.id)) byId.set(s.id, s);
    return [...byId.values()].sort((x, y) => sortKey(y) - sortKey(x) || (y.id > x.id ? 1 : -1));
  }

  // ---- WebSocket deltas ----

  onShot(m: ShotDeltaMessage): void {
    if (this.deleted.has(m.id) || this.view.items.some((s) => s.id === m.id)) return;
    const item: ShotItem = {
      id: m.id,
      occurredAt: m.occurredAt,
      receivedAt: m.receivedAt ?? Date.now(),
      source: m.source ?? null,
      meta: m.meta,
      rating: m.rating ?? null,
      note: m.note ?? null,
      cloudRev: m.cloudRev ?? 0,
      beanId: m.beanId ?? null,
      bagId: m.bagId ?? null,
      roastDay: m.roastDay ?? null,
      recipeN: m.recipeN ?? null,
      ...(m.tags ? { tags: m.tags } : {}),
      ...(m.tagsAuto ? { tagsAuto: m.tagsAuto } : {}),
    };
    this.revs.set(item.id, Math.max(this.revs.get(item.id) ?? -1, item.cloudRev));
    this.commit({ items: this.merge([item], this.view.items) });
  }

  onUpdate(m: ShotUpdateMessage): void {
    this.applyUpdate(m.id, m.cloudRev, {
      ...(m.deleted ? { deleted: true } : {}),
      ...('rating' in m ? { rating: m.rating ?? null } : {}),
      ...('note' in m ? { note: m.note ?? null } : {}),
      ...('tags' in m ? { tags: m.tags ?? [] } : {}),
      ...('tagsAuto' in m ? { tagsAuto: m.tagsAuto ?? [] } : {}),
    });
  }

  onReference(m: ReferenceMessage): void {
    if (m.cloudRev <= this.view.reference.cloudRev) return;
    const shot = m.shotId ? (this.view.curves[m.shotId] ?? null) : null;
    this.applyReference(m.shotId, m.cloudRev, shot);
    if (m.shotId && !shot) void this.curve(m.shotId);
  }

  /** Every change of a shot this store takes (HTTP answer or WebSocket delta); the filtered history follows it. */
  onChange(fn: (id: string, cloudRev: number, change: ShotChange) => void): () => void {
    this.changeListeners.add(fn);
    return () => this.changeListeners.delete(fn);
  }

  private applyUpdate(id: string, cloudRev: number, change: ShotChange): void {
    const known = this.revs.get(id) ?? -1;
    if (cloudRev <= known) return;
    this.revs.set(id, cloudRev);
    for (const fn of this.changeListeners) fn(id, cloudRev, change);
    if (change.deleted) {
      this.deleted.add(id);
      const curves = { ...this.view.curves };
      delete curves[id];
      this.commit({ items: this.view.items.filter((s) => s.id !== id), curves });
      if (this.view.reference.shotId === id)
        this.commit({ reference: { ...this.view.reference, shot: null } });
      return;
    }
    const patch = (s: ShotItem): ShotItem => ({
      ...s,
      ...('rating' in change ? { rating: change.rating ?? null } : {}),
      ...('note' in change ? { note: change.note ?? null } : {}),
      ...('tags' in change ? { tags: change.tags ?? [] } : {}),
      ...('tagsAuto' in change ? { tagsAuto: change.tagsAuto ?? [] } : {}),
      cloudRev,
    });
    const curves = this.view.curves[id]
      ? { ...this.view.curves, [id]: patch(this.view.curves[id]) }
      : this.view.curves;
    this.commit({ items: this.view.items.map((s) => (s.id === id ? patch(s) : s)), curves });
  }

  private applyReference(shotId: string | null, cloudRev: number, shot: ShotItem | null): void {
    if (cloudRev < this.view.reference.cloudRev) return;
    this.commit({
      reference: { shotId, cloudRev, shot },
      ...(shot?.curve ? { curves: { ...this.view.curves, [shot.id]: shot } } : {}),
    });
  }

  // ---- reading and changing ----

  /**
   * A bean's figures on its page (board "Eine Bohne"): its shots, how many of them balanced (rating 2), their mean
   * time in seconds, from two summaries of the cloud; null when either fails.
   */
  async beanFigures(beanId: string): Promise<{ count: number; good: number; time: number | null } | null> {
    const [all, good] = await Promise.all([
      this.api.summary(this.machineId, { bean: beanId }),
      this.api.summary(this.machineId, { bean: beanId, rating: '2' }),
    ]);
    if (!all.ok || !good.ok) return null;
    return { count: all.value.count, good: good.value.count, time: all.value.time };
  }

  /** The shot with its curve (cached). */
  async curve(id: string): Promise<ShotItem | null> {
    const cached = this.view.curves[id];
    if (cached) return cached;
    // a deleted shot is not asked for (a bean may still name it as its reference until its delta arrives)
    if (this.deleted.has(id)) return null;
    const r = await this.api.get(this.machineId, id);
    if (!r.ok) return null;
    this.commit({ curves: { ...this.view.curves, [id]: r.value } });
    if (this.view.reference.shotId === id && !this.view.reference.shot)
      this.commit({ reference: { ...this.view.reference, shot: r.value } });
    return r.value;
  }

  async rate(id: string, rating: number | null): Promise<HttpResult<ShotItem>> {
    const r = await this.api.update(this.machineId, id, { rating });
    if (r.ok) this.applyUpdate(id, r.value.cloudRev, { rating: r.value.rating });
    return r;
  }

  /** The taste tags of a shot (catalog taste-tags); the answer is taken by its cloudRev. */
  async setTags(id: string, tags: string[]): Promise<HttpResult<ShotItem>> {
    const r = await this.api.update(this.machineId, id, { tags });
    if (r.ok)
      this.applyUpdate(id, r.value.cloudRev, { tags: r.value.tags ?? [], tagsAuto: r.value.tagsAuto ?? [] });
    return r;
  }

  async setNote(id: string, note: string | null, ifRev?: number): Promise<HttpResult<ShotItem>> {
    const r = await this.api.update(this.machineId, id, { note, ...(ifRev !== undefined ? { ifRev } : {}) });
    if (r.ok) this.applyUpdate(id, r.value.cloudRev, { note: r.value.note });
    else if (r.error.code === 'rev_conflict') void this.refresh();
    return r;
  }

  async remove(id: string): Promise<HttpResult<null>> {
    const r = await this.api.remove(this.machineId, id);
    if (r.ok) this.applyUpdate(id, Number.MAX_SAFE_INTEGER, { deleted: true });
    return r;
  }

  async setReference(shotId: string | null): Promise<HttpResult<ReferenceResponse>> {
    const r = await this.api.setReference(this.machineId, shotId);
    if (r.ok) this.applyReference(r.value.shot?.id ?? null, r.value.cloudRev, r.value.shot);
    return r;
  }
}
